/**
 * ConfirmDialog — modal reusável de confirmação.
 *
 * Centraliza validação de input, loading state, mensagem de erro e atalhos de
 * teclado (Enter=confirm, Esc=cancel) para fluxos de confirmação simples.
 *
 * Para destrutivos (delete), passe `variant="destructive"` e `confirmation`
 * (string que o user deve digitar para liberar o botão — ex: nome do projeto).
 */
import type { FormEvent, JSX, ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import { ControlledInputField } from '../form/controlled-input-field.js';
import { uiText } from '../i18n.js';
import { cn } from '../lib/utils.js';
import { Button } from './button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog.js';
import { InlineAlert } from './inline-alert.js';
import { Spinner } from './spinner.js';

/** Marcador improvável de aparecer em tradução; usado para fatiar o template. */
const CONFIRMATION_TOKEN = '\u0000';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  /** Quando ausente, dialog mostra só `description` + botões (no-input mode). */
  readonly inputLabel?: string | undefined;
  readonly defaultValue?: string | undefined;
  readonly placeholder?: string | undefined;
  /** Função síncrona que valida o input. Retorna null se OK ou mensagem de erro. */
  readonly validate?: ((value: string) => string | null) | undefined;
  /** String que o user deve digitar para liberar `Confirm` (destrutivo). */
  readonly confirmation?: string | undefined;
  readonly confirmLabel?: string | undefined;
  readonly cancelLabel?: string | undefined;
  readonly variant?: 'default' | 'destructive';
  /** Mapeia o erro lançado por `onConfirm` para texto user-visible traduzido.
   *  O consumer resolve o código de erro via translator. Quando ausente, o
   *  dialog exibe `errors.unexpected` para não vazar mensagem crua. */
  readonly mapError?: ((error: unknown) => string) | undefined;
  /** Handler async; durante Promise pending mostra spinner. Lance erro para
   *  exibir InlineAlert; resolva sem erro para fechar o modal. */
  readonly onConfirm: (value: string) => Promise<void> | void;
}

/**
 * Renderiza `common.typeToConfirm` mantendo o valor a digitar num `<code>`
 * estilizado. Fatia o texto traduzido no marcador interpolado para preservar
 * prefixo/sufixo sem assumir a posição do token (varia por locale).
 */
function renderTypeToConfirm(confirmation: string): ReactNode {
  const [prefix = '', suffix = ''] = uiText('common.typeToConfirm', {
    value: CONFIRMATION_TOKEN,
  }).split(CONFIRMATION_TOKEN);
  return (
    <>
      {prefix}
      <code className="rounded bg-muted px-1 font-mono text-[11px]">{confirmation}</code>
      {suffix}
    </>
  );
}

export function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
  const {
    open,
    onOpenChange,
    title,
    description,
    inputLabel,
    defaultValue,
    placeholder,
    validate,
    confirmation,
    confirmLabel,
    cancelLabel,
    variant,
    mapError,
    onConfirm,
  } = props;

  const [value, setValue] = useState<string>(defaultValue ?? '');
  const [confirmationInput, setConfirmationInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const inputId = useId();
  const confirmationId = useId();
  const hasInput = inputLabel !== undefined;

  /* Reset ao reabrir — evita estado vazado entre instâncias do mesmo modal. */
  useEffect(() => {
    if (open) {
      setValue(defaultValue ?? '');
      setConfirmationInput('');
      setError(null);
      setBusy(false);
    }
  }, [open, defaultValue]);

  const validationError = hasInput && validate !== undefined ? validate(value) : null;
  const confirmationMatches = confirmation === undefined || confirmationInput === confirmation;
  const canConfirm = validationError === null && confirmationMatches && !busy;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(value);
      onOpenChange(false);
    } catch (e) {
      setError(mapError?.(e) ?? uiText('errors.unexpected'));
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={busy ? () => undefined : onOpenChange}>
      <DialogContent className="sm:max-w-md" locked={busy}>
        <form
          onSubmit={(e) => {
            handleSubmit(e).catch(() => undefined);
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle className={cn(variant === 'destructive' && 'text-destructive')}>
              {title}
            </DialogTitle>
            {description !== undefined && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          {hasInput && (
            <ControlledInputField
              name={inputId}
              label={inputLabel}
              value={value}
              onValueChange={setValue}
              error={validationError !== null && value.length > 0 ? validationError : undefined}
              disabled={busy}
              autoFocus
              reserveErrorSlot={false}
              {...(placeholder !== undefined && { placeholder })}
            />
          )}

          {confirmation !== undefined && (
            <ControlledInputField
              name={confirmationId}
              label={renderTypeToConfirm(confirmation)}
              value={confirmationInput}
              onValueChange={setConfirmationInput}
              disabled={busy}
              reserveErrorSlot={false}
            />
          )}

          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              {cancelLabel ?? uiText('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant={variant === 'destructive' ? 'destructive' : 'default'}
              disabled={!canConfirm}
            >
              {busy && <Spinner size="sm" className="mr-1.5" />}
              {confirmLabel ?? uiText('common.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
